import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useSignalRContext } from "../Contexts/SignalRContext";
import { GEOFENCE_QUERY_KEY } from "../Map/useGeofences";
import { BLAST_PATTERN_FENCE_QUERY_KEY } from "../Map/useBlastPatternFences";
import { DIG_BLOCK_FENCE_QUERY_KEY } from "../Map/useDigBlockFences";
import { HAUL_ROAD_QUERY_KEY } from "../Map/useHaulRoads";
import { MAP_FEATURE_QUERY_KEY } from "../Map/useMapFeatures";

// Note: drops the cached fences whenever the server says one has changed, so an open map redraws instead
// of showing an outline where it used to be. The map layers cache for 5 minutes, which is right for
// shapes that only change when somebody draws one - but it meant an edit made on the locations, blast
// patterns or ore blocks screen was invisible on the map until the cache expired, and that reads as the
// save having failed rather than as a stale cache.
//
// Note: every key is invalidated on every broadcast rather than the server saying WHICH kind of fence
// moved. They are cheap GETs of a few dozen rows each, and the alternative is a payload the server has to
// keep in step with however many map layers exist. Haul roads and map features are in here for the same
// reason, even though nothing edits either in the app yet - a layer that caches but is not invalidated is a
// layer that will one day show something that is no longer there.
//
// Note: invalidate, not refetch. A client with no map open has nothing mounted watching these keys, so
// react-query drops them and the next map to open fetches fresh - no request from the many sessions that
// are not looking at a map.
export default function GeofenceRefreshListener() {
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!connection) return;

    const handleGeofencesChanged = async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: GEOFENCE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: BLAST_PATTERN_FENCE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: DIG_BLOCK_FENCE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: HAUL_ROAD_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: MAP_FEATURE_QUERY_KEY }),
      ]);
    };

    connection.on("GeofencesChangedAsync", handleGeofencesChanged);

    return () => {
      connection.off("GeofencesChangedAsync", handleGeofencesChanged);
    };
  }, [connection, queryClient]);

  return null;
}
